import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import TabMenu from "../common/TabMenu";
import ProfileHeader from "../header/ProfileHeader";
import axios from "axios";
import Biggybear from "../../assets/images/mission-biggy-bear.0da057a.png";
import { useLiffProfile } from "../../context/LiffProfileContext";
import LoadingBar from "../LoadingBar";
import UserBcCouponCard from "./UserBcCouponCard";
import UseUserBcCouponModal from "./UseUserBcCouponModal";
import moment from "moment-timezone";
import { useNavigate, useLocation } from "react-router-dom";

const BIG_C_COUPON_TYPE_VALUE = "B,M";
const PARTNER_COUPON_TYPE_VALUE = "P";

const NEW_MEMBER_COUPON_CATEGORY = "11";
const PREMIUM_COUPON_CATEGORY = "12";
const SPECIAL_FOR_YOU_COUPON_CATEGORY = "13";

const couponStatusGroup = {
  [BIG_C_COUPON_TYPE_VALUE]: [
    {
      key: "all",
      couponStatus: "I",
      excludedCouponCategories: [
        //SPECIAL_FOR_YOU_COUPON_CATEGORY,
        NEW_MEMBER_COUPON_CATEGORY,
        //PREMIUM_COUPON_CATEGORY,
      ],
    },
     {
      key: "premium_coupons",
      couponStatus: "I",
      couponCategories: [PREMIUM_COUPON_CATEGORY],
    },
    {
      key: "special_for_you",
      couponStatus: "I",
      couponCategories: [SPECIAL_FOR_YOU_COUPON_CATEGORY],
    },
    {
      key: "new_member",
      couponStatus: "I",
      couponCategories: [NEW_MEMBER_COUPON_CATEGORY],
    },
    { key: "used_expired", couponStatus: "E,U" },
  ],
  [PARTNER_COUPON_TYPE_VALUE]: [
    {
      key: "all",
      couponStatus: "I",
      excludedCouponCategories: [
        //SPECIAL_FOR_YOU_COUPON_CATEGORY,
        NEW_MEMBER_COUPON_CATEGORY,
        //PREMIUM_COUPON_CATEGORY,
      ],
    },
     {
      key: "premium_coupons",
      couponStatus: "I",
      couponCategories: [PREMIUM_COUPON_CATEGORY],
    },
    {
      key: "special_for_you",
      couponStatus: "I",
      couponCategories: [SPECIAL_FOR_YOU_COUPON_CATEGORY],
    },
    {
      key: "new_member",
      couponStatus: "I",
      couponCategories: [NEW_MEMBER_COUPON_CATEGORY],
    },
    { key: "used_expired", couponStatus: "E,U" },
  ],
};

export default function MyCoupons() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialCouponType = searchParams.get("couponType");
  const initialCategory = searchParams.get("category");

  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation();
  const { authToken, userId } = useLiffProfile();
  const [activeTab, setActiveTab] = useState("bigc");

  const seniorLayout = localStorage.getItem("seniorLayout") === "true";

  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const [loadingCoupon, setLoadingCoupon] = useState(false);

  const [userBcCoupons, setUserBcCoupons] = useState([]);
  const [totalUserBcCoupons, setTotalUserBcCoupons] = useState(0);
  const [userBcCouponsInfo, setUserBcCouponsInfo] = useState([]);
  const [activeCouponStatus, setActiveCouponStatus] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [showUseCouponModal, setShowUseCouponModal] = useState(false);

  const [searchingParams, setSearchingParams] = useState(null);

  const tabs = [
    {
      key: "bigc",
      label: t("bigc_coupons"),
      couponType: BIG_C_COUPON_TYPE_VALUE,
    },
    {
      key: "partner",
      label: t("partner_coupons"),
      couponType: PARTNER_COUPON_TYPE_VALUE,
    },
  ];

  const apiUrl =
    process.env.REACT_APP_API_URL + "/api/users/current/clm-coupons/";
  const clientAuth = process.env.REACT_APP_CLIENT_AUTH;

  const headers = {
    Authorization: "Bearer " + authToken,
    "X-Client-Auth": clientAuth,
    "x-user-id": userId,
    "Accept-Language": i18n.language.toUpperCase(),
  };

  const fetchData = async (pageNo, overrideParams = null) => {
    setLoading(true);
    try {
      const sourceParams = overrideParams || searchingParams;

      const rawParams = {
        ...sourceParams,
        pageNo,
        couponCategories: sourceParams.couponCategories?.join(","),
        excludedCouponCategories:
          sourceParams.excludedCouponCategories?.join(","),
      };

      const params = new URLSearchParams();
      Object.entries(rawParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${apiUrl}?${params.toString()}`, {
        headers,
      });

      const responseData = response.data?.data || { items: [], total: 0 };
      setUserBcCoupons((prev) => [...prev, ...responseData.items]);
      setTotalUserBcCoupons(responseData.total);
      setUserBcCouponsInfo(responseData.info);
      setError(null);

      //  อัปเดตหน้าใหม่เข้า state
      if (!overrideParams) {
        setSearchingParams((prev) => ({ ...prev, pageNo }));
      }
    } catch (err) {
      setError(err.message);
      setUserBcCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const tabKey = initialCouponType === "P" ? "partner" : "bigc";
    const tab = tabs.find((t) => t.key === tabKey);
    const couponType = tab?.couponType || BIG_C_COUPON_TYPE_VALUE;
    const group = couponStatusGroup[couponType];

    // ส่ง Event ไปยัง GTM/GA4
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "my_coupon_view", // ชื่อ event ที่จะไปใช้ใน GTM
      user_id: userId, // สามารถส่งข้อมูลเพิ่ม เช่น userId
      coupon_type: tabKey, // ส่งประเภทคูปอง
    });

    let defaultStatusItem = group[0];
    if (initialCategory) {
      const matched = group.find((g) => g.key === initialCategory);
      if (matched) defaultStatusItem = matched;
    }

    const newParams = {
      couponType,
      pageNo: 1,
      pageSize: 10,
      couponStatus: defaultStatusItem.couponStatus,
      couponCategories: defaultStatusItem.couponCategories || [],
      excludedCouponCategories:
        defaultStatusItem.excludedCouponCategories || [],
    };

    setActiveTab(tabKey);
    setActiveCouponStatus(defaultStatusItem);
    setSearchingParams(newParams);
    fetchData(1, newParams);
  }, []);

  const filteredCouponStatusTabs =
    (searchingParams?.couponType &&
      couponStatusGroup[searchingParams.couponType]?.filter((tab) => {
        if (!tab.couponCategories) return true;
        return tab.couponCategories.some((ctg) =>
          userBcCouponsInfo?.couponCategories?.includes(ctg)
        );
      })) ||
    [];

  const filteredUserBcCoupons =
    searchingParams?.couponStatus === "I"
      ? (userBcCoupons || []).filter((coupon) =>
          moment().isSameOrBefore(moment(coupon.expireDate), "day")
        )
      : userBcCoupons || [];

  const handleCouponTypeClick = async (key) => {
    const selectedTab = tabs.find((tab) => tab.key === key);
    if (!selectedTab) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "my_coupon_view", // ชื่อ event ที่จะไปใช้ใน GTM
      user_id: userId, // สามารถส่งข้อมูลเพิ่ม เช่น userId
      coupon_type: key, // ส่งประเภทคูปอง
    });
    const group = couponStatusGroup[selectedTab.couponType];
    const defaultStatusItem = group[0];
    const newParams = {
      couponType: selectedTab.couponType,
      couponStatus: defaultStatusItem.couponStatus,
      couponCategories: defaultStatusItem.couponCategories || [],
      excludedCouponCategories:
        defaultStatusItem.excludedCouponCategories || [],
      pageNo: 1,
      pageSize: 10,
    };

    setActiveTab(key);
    setActiveCouponStatus(defaultStatusItem);
    setUserBcCoupons([]);
    setSearchingParams(newParams);
    await fetchData(1, newParams);
  };

  const handleCouponStatusClick = async (index) => {
    const selectedStatus = filteredCouponStatusTabs[index];
    if (selectedStatus.couponStatus.startsWith("https")) {
      window.open(selectedStatus.couponStatus, "_blank", "noopener,noreferrer");
      return;
    }

    const newParams = {
      ...searchingParams,
      couponStatus: selectedStatus.couponStatus,
      couponCategories: selectedStatus.couponCategories,
      excludedCouponCategories: selectedStatus.excludedCouponCategories,
      pageNo: 1,
    };

    setActiveCouponStatus(selectedStatus);
    setUserBcCoupons([]);
    setSearchingParams(newParams);
    await fetchData(1, newParams);
  };

  const handleScroll = async (e) => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 1;
    const hasMore = userBcCoupons.length < totalUserBcCoupons;

    if (isNearBottom && !loadingCoupon && hasMore) {
      setLoadingCoupon(true);
      await fetchData((searchingParams.pageNo || 1) + 1);
      setLoadingCoupon(false);
    }
  };

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="h-screen flex flex-col bg-secondary-background-gray">
        <ProfileHeader className="bg-primary" />
        <div
          className="flex-grow overflow-y-auto"
          ref={scrollRef}
          onScroll={handleScroll}
        >
          <div className="w-full flex flex-col gap-y-4 bg-secondary-background py-5 sm:py-4">
            <TabMenu
              tabs={tabs.map((tab) => ({
                key: tab.key,
                value: tab.key,
                label: tab.label,
              }))}
              value={activeTab}
              onTabClick={handleCouponTypeClick}
            />
            <div className="w-full min-h-[2.5rem] px-2 sm:px-0 flex justify-between text-lg sm:text-base font-medium overflow-x-auto no-scrollbars">
              {filteredCouponStatusTabs.map((tab, index) => (
                <div
                  key={`coupon-status-tab-${index}`}
                  className={`h-10 flex-1 px-5 flex justify-center items-center rounded-full cursor-pointer whitespace-nowrap ${
                    activeCouponStatus === tab ? "bg-primary" : ""
                  }`}
                  onClick={() => handleCouponStatusClick(index)}
                >
                  {t(tab.key)}
                </div>
              ))}
            </div>
          </div>
          <div className="w-full flex flex-col p-3 sm:p-2 gap-5 sm:gap-2 bg-secondary-background-gray">
            {filteredUserBcCoupons.length > 0 ? (
              filteredUserBcCoupons.map((coupon, index) => (
                <UserBcCouponCard
                  key={`my-coupon-${index}`}
                  value={coupon}
                  showButton
                  onUseCoupon={(couponId) => {
                    const coupon = userBcCoupons.find((c) => c.id === couponId);
                    if (
                      coupon?.couponType?.includes("P") ||
                      coupon?.couponType?.includes("M")
                    ) {
                      setSelectedCoupon(coupon);
                      setShowUseCouponModal(true);
                    } else {
                      navigate(`/my-coupons/${couponId}`);
                    }
                  }}
                />
              ))
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center">
                {/* <img src={Biggybear} alt="No Coupons" className="w-40 mt-20" /> */}
              </div>
            )}
          </div>
        </div>
      </div>
      <UseUserBcCouponModal
        show={showUseCouponModal}
        value={selectedCoupon}
        seniorLayout={seniorLayout === "true"}
        onClose={() => setShowUseCouponModal(false)}
        onConfirm={() => {
          navigate(`/my-coupons/${selectedCoupon?.id}`);
          setShowUseCouponModal(false);
        }}
      />
      <LoadingBar loading={loading} label="Loading..." />
    </div>
  );
}
